/**
 * MenuItemPerformanceTab - Comprehensive menu item sales analysis
 * Shows all items sold by day, week, month with quantity and revenue
 * Sorted from best to worst performers
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, BarChart3, 
  Calendar, RefreshCw, Download, Search, ChevronDown, ChevronUp,
  ArrowUpDown, Coffee, UtensilsCrossed, IceCream, Salad
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

const CategoryIcon = ({ category }) => {
  const iconClass = "w-4 h-4";
  if (category?.toLowerCase().includes('coffee') || category?.toLowerCase().includes('drink')) {
    return <Coffee className={iconClass} />;
  }
  if (category?.toLowerCase().includes('food') || category?.toLowerCase().includes('lunch')) {
    return <UtensilsCrossed className={iconClass} />;
  }
  if (category?.toLowerCase().includes('smoothie') || category?.toLowerCase().includes('bowl')) {
    return <IceCream className={iconClass} />;
  }
  return <Salad className={iconClass} />;
};

export default function MenuItemPerformanceTab() {
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [itemData, setItemData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('quantity'); // quantity, revenue, name
  const [sortDir, setSortDir] = useState('desc');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/analytics/menu-item-performance?period=${period}`);
      setItemData(res.data.items || []);
      setSummary(res.data.summary || {});
      
      // Extract unique categories
      const cats = [...new Set((res.data.items || []).map(i => i.category).filter(Boolean))];
      setCategories(cats);
    } catch (err) {
      console.error('Failed to fetch menu performance:', err);
      toast.error('Failed to load menu performance data');
    }
    setLoading(false);
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort data
  const filteredData = itemData
    .filter(item => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'quantity') {
        aVal = a.quantity_sold || 0;
        bVal = b.quantity_sold || 0;
      } else if (sortBy === 'revenue') {
        aVal = a.total_revenue || 0;
        bVal = b.total_revenue || 0;
      } else {
        aVal = a.name || '';
        bVal = b.name || '';
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const exportToCSV = () => {
    const headers = ['Rank', 'Item Name', 'Category', 'Quantity Sold', 'Unit Price', 'Total Revenue'];
    const rows = filteredData.map((item, idx) => [
      idx + 1,
      item.name,
      item.category || 'Uncategorized',
      item.quantity_sold,
      item.unit_price?.toFixed(2) || '0.00',
      item.total_revenue?.toFixed(2) || '0.00'
    ]);
    
    const csvContent = [
      `Menu Item Performance Report - ${period.toUpperCase()}`,
      `Generated: ${new Date().toLocaleString()}`,
      '',
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `menu_performance_${period}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded!');
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'today': return "Today's";
      case 'yesterday': return "Yesterday's";
      case 'week': return "This Week's";
      case 'month': return "This Month's";
      case 'last_month': return "Last Month's";
      default: return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Menu Item Performance</h2>
          <p className="text-muted-foreground">See which items are selling and their revenue</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV} disabled={loading || !filteredData.length}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Period Selector */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Time Period</label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="period-select">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Category</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="lg:col-span-2">
              <label className="text-sm font-medium text-muted-foreground mb-1 block">Search Items</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search menu items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Items Sold</p>
                <p className="text-2xl font-bold">{summary?.total_quantity?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gross Revenue</p>
                <p className="text-2xl font-bold">${summary?.total_revenue?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-muted-foreground">incl. VAT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Revenue</p>
                <p className="text-2xl font-bold">${(summary?.total_revenue / 1.125)?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-muted-foreground">excl. 12.5% VAT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unique Items</p>
                <p className="text-2xl font-bold">{summary?.unique_items || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Note about Dashboard comparison */}
      <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
        <strong>Note:</strong> Dashboard shows Net Revenue (after VAT removed). This report shows item-level sales. 
        The Net Revenue here should match Dashboard when viewing the same time period.
      </div>

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {getPeriodLabel()} Menu Item Sales
            {filteredData.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({filteredData.length} items)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No sales data found for this period</p>
              <p className="text-sm">Try selecting a different time period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rank</th>
                    <th 
                      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => toggleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Item Name
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Category</th>
                    <th 
                      className="text-right py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => toggleSort('quantity')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Qty Sold
                        <ArrowUpDown className="w-3 h-3" />
                        {sortBy === 'quantity' && (sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                      </div>
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Unit Price</th>
                    <th 
                      className="text-right py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => toggleSort('revenue')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Total Revenue
                        <ArrowUpDown className="w-3 h-3" />
                        {sortBy === 'revenue' && (sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, idx) => (
                    <tr 
                      key={item.name || idx} 
                      className="border-b border-border/50 hover:bg-muted/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          idx === 1 ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' :
                          idx === 2 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-foreground">{item.name}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CategoryIcon category={item.category} />
                          {item.category || 'Uncategorized'}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-semibold text-foreground">{item.quantity_sold?.toLocaleString() || 0}</span>
                      </td>
                      <td className="py-3 px-4 text-right text-muted-foreground">
                        ${item.unit_price?.toFixed(2) || '0.00'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-bold text-green-600 dark:text-green-400">
                          ${item.total_revenue?.toFixed(2) || '0.00'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/50 font-semibold">
                    <td colSpan={3} className="py-3 px-4 text-right">Totals:</td>
                    <td className="py-3 px-4 text-right">
                      {filteredData.reduce((sum, i) => sum + (i.quantity_sold || 0), 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4 text-right text-green-600 dark:text-green-400">
                      ${filteredData.reduce((sum, i) => sum + (i.total_revenue || 0), 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
